import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useBridge } from '../../bridge/useBridge';
import { useRouteSync } from '../../bridge/useRouteSync';
import { usePaymentList } from './usePaymentList';
import { PaymentListStep } from './steps/PaymentListStep';
import { PaymentDetailStep } from './steps/PaymentDetailStep';

/**
 * pay-by-bank-backoffice (PISPBO) — internal treasury back-office surface for
 * Hefame operators to monitor consumer Pay-by-Bank revenue collections.
 *
 * Two independent, mutually-navigable screens (list + detail), not a linear
 * wizard, so it uses plain `<Routes>` instead of `StepWizard` (LESSONS → "skip
 * StepWizard on flows with independent entry points"). The list query/filter
 * state lives here at the flow root via `usePaymentList`, so it survives in-app
 * navigation to the detail screen and back (PRD O-12). `readiness: 'manual'`:
 * each screen signals readiness once its first load settles, so the host
 * overlay never reveals an internal Spinner.
 *
 * See `flows/pay-by-bank-backoffice/PRD.md` and `API_CONTRACT.md`.
 */
export const PayByBankBackofficeFlow: React.FC = () => {
  const bridge = useBridge();
  // Mirror intra-flow route (list / detail/:id) into the host URL bar.
  useRouteSync('pay-by-bank-backoffice');
  // List query/filter state is owned here so it survives in-app navigation to
  // the detail screen and back (PRD O-12); the screens own their own routing.
  const list = usePaymentList(bridge.locale ?? 'en');

  return (
    <Routes>
      <Route path="/" element={<Navigate to="list" replace />} />
      <Route path="list" element={<PaymentListStep list={list} />} />
      <Route path="detail/:revenueId" element={<PaymentDetailStep />} />
      <Route path="*" element={<Navigate to="list" replace />} />
    </Routes>
  );
};

export default PayByBankBackofficeFlow;
